import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Chip,
  IconButton,
  Menu,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { isEmpty } from "utils/helpers";


export default function ScheduleListingPage({
  examSchedulesData,
  limit,
  setLimit,
  setPage,
  page,
}) {
  const [entriesPerPage, setEntriesPerPage] = useState(limit);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const history = useHistory()

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderSelectedLabels = (selectedIds: any, options: any): string => {
    const names = options
      .filter((opt) => selectedIds.includes(opt.id))
      .map((opt) => opt.name);

    if (names?.length === 0) return "";

    const extraCount = names?.length > 1 ? `+${names?.length - 1}` : "";
    return names.join(", ").replace(/(.{15})..+/, `$1...${extraCount}`);
  };
  const handleEdit=(obj)=>{
     history.push(`/exam_schedule/update_exam/${obj?.id}`)
  }
  const handleAddSyllabus=(obj)=>{
     history.push(`/exam_schedule/add_syllabus/${obj?.id}`)
  }
  const handledeliveries=(obj)=>{
     history.push(`/exam_schedule/delivery_list/${obj?.id}`)
  }

  // Calculate pagination
  const showPagination = examSchedulesData?.length === limit;
  const hasNextPage = examSchedulesData?.length === limit;

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5" }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Show Entries */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">Show</Typography>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={limit}
              onChange={(e: any) => setLimit(e.target.value)}
              variant="outlined"
            >
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2">Entries</Typography>
        </Box>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
              <TableRow>
                <TableCell>
                  <strong>Schedule Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Date</strong>
                </TableCell>
                <TableCell>
                  <strong>Class</strong>
                </TableCell>
                <TableCell>
                  <strong>Created at</strong>
                </TableCell>
                <TableCell>
                  <strong>Created by</strong>
                </TableCell>
                <TableCell>
                  <strong>Updated at</strong>
                </TableCell>
                <TableCell>
                  <strong>Updated by</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {examSchedulesData?.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>{schedule.schedule_name}</TableCell>
                  <TableCell>{formatDate(schedule.schedule_date)}</TableCell>
                  <TableCell>
                    {schedule.class_names.map((className, index) => (
                      <Chip
                        key={index}
                        label={className}
                        size="small"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>{formatDateTime(schedule.created_at)}</TableCell>
                  <TableCell>{schedule.created_by}</TableCell>
                  <TableCell>
                    {schedule.updated_at
                      ? formatDateTime(schedule.updated_at)
                      : "-"}
                  </TableCell>
                  <TableCell>{schedule.updated_by || "-"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        sx={{ textTransform: "none" }}
                        onClick={()=>{
                          handleEdit(schedule)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AssignmentIcon />}
                        sx={{ textTransform: "none" }}
                        onClick={()=>{
                          handleAddSyllabus(schedule)
                        }}
                      >
                        Add Syllabus
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QuizIcon />}
                        sx={{ textTransform: "none" }}
                        disabled={(!schedule.deliverable || isEmpty(schedule?.syllabus_public_pdf_url))}
                        onClick={()=>{
                          handledeliveries(schedule)
                        }}
                      >
                        Test Deliveries
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Schedule</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Syllabus</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <QuizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Test Deliveries</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
